import { ChangeDetectorRef, Component, DestroyRef, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { setControlDisabled } from '../../../shared/utils/set-control-disabled';
import { EntityData } from '@models/companies/entity-data';
import {
  CorporateHolderModel,
  IndividualHolderModel,
  SecurityRegistryRecord
} from '@models/shares/security-registry-record';
import { acnValidator } from '../../../shared/validators/acn.validator';
import { companyNameValidator } from '../../../shared/validators/compny-name.validator';
import { CheckboxComponent } from '@shared/controls/checkbox/checkbox.component';
import { InputComponent } from '@shared/controls/input/input.component';
import { SelectGroupsComponent } from '@shared/controls/select-groups/select-groups.component';
import { YesNoControlComponent } from '@shared/controls/yes-no-control-component/yes-no-control.component';
import {
  SelectShareholderComponent
} from '../../controls/select-shareholder/select-shareholder.component';
import { ShareholderTypesEnum, shareholderTypesOptionGroups } from '../../models/shares/company-security-issue.model';
import {
  ShareJointSecurityHolderComponent
} from '../../controls/share-joint-security-holder/share-joint-security-holder.component';
import {
  IndividualDataFormGroup,
  IndividualDataFormGroupComponent, IndividualDataFormGroupControls
} from "../../reusable-form-groups/individual-data-form-group/individual-data-form-group.component";
import {
  CompanyNameAcnFormGroupComponent
} from "../../reusable-form-groups/company-name-acn-form-group/company-name-acn-form-group.component";
import {
  AddressFormGroupComponent,
  AddressFormGroup,
  AddressFormGroupControls
} from "../../reusable-form-groups/address-form-group/address-form-group.component";
import { groupSecurityRecordsByName } from '../../../shared/utils/security.helper';
import { EntitySecurityTransfer } from '@models/security-transactions/entity-security-transfer';
import { CountriesService } from "../../../shared/services/countries.service";
import { CompanySecurityTransfer } from "../../models/shares";
import { Company } from "@models/companies/company";

export interface SelectShareholderFormControls {
  shareholderType: FormControl<ShareholderTypesEnum | null>;

  securityRegistryRecordGroup: FormControl<SecurityRegistryRecord[] | null>;
  isNotBeneficialOwner: FormControl<boolean | null>;
  beneficialOwner: FormControl<string | null>;

  newIndividualShareholder: IndividualDataFormGroup;

  name: FormControl<string | null>;
  acn: FormControl<string | null>;
  isOverseasCompany: FormControl<boolean>;
  registeredAddress: AddressFormGroup;

  joint: FormControl<(IndividualHolderModel | CorporateHolderModel)[] | null>;
}

@Component({
  selector: 'app-select-shareholder-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    InputComponent,
    CheckboxComponent,
    YesNoControlComponent,
    SelectGroupsComponent,
    SelectShareholderComponent,
    IndividualDataFormGroupComponent,
    CompanyNameAcnFormGroupComponent,
    AddressFormGroupComponent,
    ShareJointSecurityHolderComponent,
  ],
  templateUrl: './select-shareholder-form.component.html',
  styleUrl: './select-shareholder-form.component.scss'
})
export class SelectShareholderFormComponent implements OnInit, OnChanges {
  destroyRef = inject(DestroyRef);
  cdr = inject(ChangeDetectorRef);

  @Input({ required: true }) formGroup!: FormGroup<SelectShareholderFormControls>;
  @Input() securityRegistryRecords: SecurityRegistryRecord[] = [];
  @Input() selectedIndividualShareholder: IndividualHolderModel | null = null;
  @Input() selectedCompanyShareholder: EntityData | null = null;

  readonly shareholderTypesOptionGroups = shareholderTypesOptionGroups;
  readonly ShareholderTypesEnum = ShareholderTypesEnum;
  readonly hiddenIndividualControls: (keyof IndividualDataFormGroupControls)[] = ['formerName', 'dob', 'birthCity', 'birthCountry'];
  readonly individualDataFormLabels: Record<keyof Partial<IndividualDataFormGroupControls>, string> = { fullName: 'Individual Name' } as Record<keyof Partial<IndividualDataFormGroupControls>, string>;
  readonly addressFormLabels: Record<keyof Partial<AddressFormGroupControls>, string> = { normalizedFullAddress: 'Registered address' } as Record<keyof Partial<AddressFormGroupControls>, string>;

  groupedSecurityRegistryRecords: SecurityRegistryRecord[][] = [];

  ngOnInit(): void {
    this.listenShareholderTypeChanges();
    this.listenIsSharesOwnedOnBehalfChanges();
    this.listenIsOverseasCompanyChange();
    this.securityRegistryRecordGroupChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['securityRegistryRecords']) {
      this.groupedSecurityRegistryRecords = groupSecurityRecordsByName(this.securityRegistryRecords);
    }
  }

  private listenShareholderTypeChanges(): void {
    this.formGroup.controls.shareholderType.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((shareholderType) => {
        if (shareholderType === null) {
          return;
        }

        // set state of the existing security holder
        setControlDisabled(this.formGroup.controls.securityRegistryRecordGroup, shareholderType !== ShareholderTypesEnum.ExistingShareholder);

        // set state of the new Individual Shareholder control
        setControlDisabled(this.formGroup.controls.newIndividualShareholder, shareholderType !== ShareholderTypesEnum.Individual);
        this.hiddenIndividualControls.forEach(key => setControlDisabled(this.formGroup.controls.newIndividualShareholder.controls[key]));

        // set state of the new Corporate Shareholder control
        setControlDisabled(this.formGroup.controls.acn, shareholderType !== ShareholderTypesEnum.Corporate);
        setControlDisabled(this.formGroup.controls.name, shareholderType !== ShareholderTypesEnum.Corporate);
        setControlDisabled(this.formGroup.controls.isOverseasCompany, shareholderType !== ShareholderTypesEnum.Corporate);
        setControlDisabled(this.formGroup.controls.registeredAddress, shareholderType !== ShareholderTypesEnum.Corporate);

        // set state of the new Joint Shareholder control
        setControlDisabled(this.formGroup.controls.joint, shareholderType !== ShareholderTypesEnum.JointShareholder);

        this.formGroup.controls.isNotBeneficialOwner.setValue(this.formGroup.controls.isNotBeneficialOwner.value ?? null);

        this.cdr.detectChanges();
      });
  }

  private listenIsSharesOwnedOnBehalfChanges(): void {
    this.formGroup.controls.isNotBeneficialOwner.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isSharesOwnedOnBehalf) => {
        setControlDisabled(this.formGroup.controls.beneficialOwner, !isSharesOwnedOnBehalf);
      });
  }

  private listenIsOverseasCompanyChange(): void {
    this.formGroup.controls.isOverseasCompany.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isOverseasCompany) => {
        if (isOverseasCompany) {
          this.formGroup.controls.acn.clearValidators();
          this.formGroup.controls.name.setValidators([Validators.required, Validators.minLength(7)]);
        } else {
          this.formGroup.controls.acn.setValidators([Validators.required, acnValidator()]);
          this.formGroup.controls.name.setValidators([Validators.required, Validators.minLength(7), companyNameValidator()]);
        }

        this.cdr.detectChanges();
        this.formGroup.updateValueAndValidity();
        this.formGroup.controls.acn.updateValueAndValidity();
        this.formGroup.controls.acn.patchValue(this.formGroup.value.acn ?? '');
      });
  }

  private securityRegistryRecordGroupChanges(): void {
    this.formGroup.controls.securityRegistryRecordGroup.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((securityRegistryRecordGroup) => {
        if (!securityRegistryRecordGroup || !securityRegistryRecordGroup[0]) return;

        const record = securityRegistryRecordGroup[0];

        this.formGroup.controls.isNotBeneficialOwner.setValue(!record.isBeneficialOwner);
        this.formGroup.controls.beneficialOwner.setValue(record.beneficialOwner);
      });
  }

  static defineForm(): FormGroup<SelectShareholderFormControls> {
    return new FormGroup({
      shareholderType: new FormControl<ShareholderTypesEnum | null>(null, [Validators.required]),

      securityRegistryRecordGroup: new FormControl<SecurityRegistryRecord[] | null>(null, [Validators.required]),
      isNotBeneficialOwner: new FormControl<boolean | null>(null, [Validators.required]),
      beneficialOwner: new FormControl<string | null>(null, [Validators.required]),

      newIndividualShareholder: IndividualDataFormGroupComponent.defineForm(),

      name: new FormControl<string | null>(null, [Validators.required]),
      acn: new FormControl<string | null>(null, [Validators.required]),
      isOverseasCompany: new FormControl<boolean>(false, [Validators.required]),
      registeredAddress: AddressFormGroupComponent.defineForm(),

      joint: new FormControl<(IndividualHolderModel | CorporateHolderModel)[] | null>(null, [Validators.required])
    }) as FormGroup<SelectShareholderFormControls>;
  }

  setupChange(change: CompanySecurityTransfer): void {
    const selectedSecurityRegistryRecordGroup = this.groupedSecurityRegistryRecords
      .find((group) => group.some(record => record?.holders
        .every(holder => change.transferTo.relationshipIds?.includes(holder.relationshipId))));

    this.formGroup.controls.securityRegistryRecordGroup.setValue(selectedSecurityRegistryRecordGroup ?? null);

    // set the shareholder type
    if (selectedSecurityRegistryRecordGroup) {
      this.formGroup.controls.shareholderType.setValue(ShareholderTypesEnum.ExistingShareholder);
      this.formGroup.controls.securityRegistryRecordGroup.setValue(selectedSecurityRegistryRecordGroup ?? null);
      this.cdr.detectChanges();
    } else if (change.securityHoldersTo.length >= 2) {
      this.formGroup.controls.shareholderType.setValue(ShareholderTypesEnum.JointShareholder);
      this.formGroup.controls.joint.setValue(new EntitySecurityTransfer(change).securityHoldersTo);
      this.formGroup.controls.isNotBeneficialOwner.setValue(!change.securityHoldersTo[0].details.isBeneficialOwner);
      this.formGroup.controls.beneficialOwner.setValue(change.securityHoldersTo[0].details.beneficialOwner);
    } else if (change.securityHoldersTo.length === 1) {
      const holder = change.securityHoldersTo[0];

      if (holder.$type === IndividualHolderModel.$type) {
        this.selectedIndividualShareholder = holder as IndividualHolderModel;
        this.formGroup.controls.shareholderType.setValue(ShareholderTypesEnum.Individual);
        this.formGroup.controls.newIndividualShareholder.patchValue(this.selectedIndividualShareholder.individualData);
      } else {
        this.selectedCompanyShareholder = (holder as CorporateHolderModel).entityData;
        this.formGroup.controls.shareholderType.setValue(ShareholderTypesEnum.Corporate);
        this.formGroup.controls.isOverseasCompany.setValue(CountriesService.isAustralianStateOrAustralia(this.selectedCompanyShareholder.registeredAddress.country));
        this.formGroup.patchValue({
          name: this.selectedCompanyShareholder.name,
          acn: this.selectedCompanyShareholder.entityNumber,
          registeredAddress: this.selectedCompanyShareholder.registeredAddress
        });
      }

      this.formGroup.controls.isNotBeneficialOwner.setValue(!holder.details.isBeneficialOwner);
      this.formGroup.controls.beneficialOwner.setValue(holder.details.beneficialOwner);
    }

    this.formGroup.controls.shareholderType.setValue(this.formGroup.value.shareholderType ?? null);
  }

  onCompanySelected(company: Company): void {
    this.formGroup.patchValue({
      name: company?.name,
      acn: company?.acn,
      isOverseasCompany: !CountriesService.isAustralianStateOrAustralia(company?.registeredAddress?.country),
      registeredAddress: company?.registeredAddress,
    })
  }
}
